import {
	getPrimaryPhaseMetricLabel,
	isPrimaryPhaseCurrencyMetric,
	isPrimaryPhasePercentMetric,
	ORDER_PRIMARY_PHASE_METRICS,
} from '../../pipeline/lib/orderPrimaryPhaseMetric';
import {
	formatTrendYAxisMetricsLabel,
	TREND_Y_AXIS_METRIC,
	normalizeTrendYAxisMetrics,
	resolveTrendPeriodValueField,
	resolvePrimaryTrendYAxisMetric,
} from './trendYAxisMetrics';

export { TREND_Y_AXIS_METRIC_OPTIONS, TREND_MAX_Y_AXIS_METRICS, TREND_Y_AXIS_METRIC } from './trendYAxisMetrics';

const PERIOD_YOY_GRANULARITY = {
	MONTH: 'month',
	QUARTER: 'quarter',
};

const PERIOD_YOY_VALUE_FIELD = {
	COUNT: 'count',
	TOTAL_PRICE: 'totalPrice',
};

/** 图表系列色统一见 common/lib/statisticsChartPalette.js */

export const CHART_PANELS = [
	{ key: 'count', valueField: PERIOD_YOY_VALUE_FIELD.COUNT, chartMetric: 'count', title: '单数' },
	{
		key: 'amount',
		valueField: PERIOD_YOY_VALUE_FIELD.TOTAL_PRICE,
		chartMetric: ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE,
		title: '合同金额',
	},
];

export const TREND_MULTI_PANELS = [
	{ key: 'multi-month', valueField: null, granularity: PERIOD_YOY_GRANULARITY.MONTH, title: '各月', multiMetric: true },
	{ key: 'multi-quarter', valueField: null, granularity: PERIOD_YOY_GRANULARITY.QUARTER, title: '各季', multiMetric: true },
];

export const TREND_MULTI_SINGLE_PANEL = [{ key: 'multi', title: '', multiMetric: true }];

export const TREND_CHART_TYPE = {
	LINE: 'line',
	BAR: 'bar',
	MAP: 'map',
};

export const TREND_CHART_TYPE_OPTIONS = [
	{ value: TREND_CHART_TYPE.LINE, label: '折线' },
	{ value: TREND_CHART_TYPE.BAR, label: '柱状' },
	{ value: TREND_CHART_TYPE.MAP, label: '地图' },
];

export const TREND_LINE_SHAPE = {
	SMOOTH: 'smooth',
	STRAIGHT: 'straight',
};

export const TREND_LINE_SHAPE_OPTIONS = [
	{ value: TREND_LINE_SHAPE.SMOOTH, label: '曲线' },
	{ value: TREND_LINE_SHAPE.STRAIGHT, label: '直线' },
];

export const DUAL_CHART_PANELS = [
	{
		key: 'count-month',
		valueField: PERIOD_YOY_VALUE_FIELD.COUNT,
		granularity: PERIOD_YOY_GRANULARITY.MONTH,
		title: '单数 · 各月',
		currencyMetric: false,
	},
	{
		key: 'amount-month',
		valueField: PERIOD_YOY_VALUE_FIELD.TOTAL_PRICE,
		granularity: PERIOD_YOY_GRANULARITY.MONTH,
		title: '合同金额 · 各月',
		currencyMetric: true,
	},
	{
		key: 'count-quarter',
		valueField: PERIOD_YOY_VALUE_FIELD.COUNT,
		granularity: PERIOD_YOY_GRANULARITY.QUARTER,
		title: '单数 · 各季',
		currencyMetric: false,
	},
	{
		key: 'amount-quarter',
		valueField: PERIOD_YOY_VALUE_FIELD.TOTAL_PRICE,
		granularity: PERIOD_YOY_GRANULARITY.QUARTER,
		title: '合同金额 · 各季',
		currencyMetric: true,
	},
];

export const isTrendBarChart = (chartType) => chartType === TREND_CHART_TYPE.BAR;

export const isTrendMapChart = (chartType) => chartType === TREND_CHART_TYPE.MAP;

export const resolveCompareLineSmooth = (lineShape = TREND_LINE_SHAPE.SMOOTH) =>
	lineShape === TREND_LINE_SHAPE.STRAIGHT ? false : 0.35;

const isLineCompareAmountMetric = (metric) =>
	metric === TREND_Y_AXIS_METRIC.TOTAL_PRICE || metric === 'amount';

const buildSingleMetricPanel = (metric) => ({
	key: metric,
	valueField: resolveTrendPeriodValueField(metric),
	chartMetric: metric,
	title: getPrimaryPhaseMetricLabel(metric),
	currencyMetric: isPrimaryPhaseCurrencyMetric(metric),
	percentMetric: isPrimaryPhasePercentMetric(metric),
});

const buildDualFullPeriodPanels = (metric) => {
	const panel = buildSingleMetricPanel(metric);
	return [
		{
			...panel,
			key: `${metric}-month`,
			granularity: PERIOD_YOY_GRANULARITY.MONTH,
			title: `${panel.title} · 各月`,
		},
		{
			...panel,
			key: `${metric}-quarter`,
			granularity: PERIOD_YOY_GRANULARITY.QUARTER,
			title: `${panel.title} · 各季`,
		},
	];
};

/** 按纵轴指标解析当前应展示的折线 panel（双整年模式为月+季各一图） */
export const resolveLineCompareActivePanels = (yAxisMetricOrMetrics = TREND_Y_AXIS_METRIC.COUNT, { dualFullPeriod = false } = {}) => {
	const metrics = normalizeTrendYAxisMetrics(yAxisMetricOrMetrics);
	const multiMetric = metrics.length > 1;

	if (multiMetric) {
		if (dualFullPeriod) {
			return TREND_MULTI_PANELS.map((panel) => ({
				...panel,
				title: `${formatTrendYAxisMetricsLabel(metrics)} · ${panel.title}`,
			}));
		}
		return [
			{
				key: 'multi',
				title: formatTrendYAxisMetricsLabel(metrics),
				multiMetric: true,
			},
		];
	}

	const yAxisMetric = resolvePrimaryTrendYAxisMetric(metrics);
	if (dualFullPeriod) {
		if (yAxisMetric === ORDER_PRIMARY_PHASE_METRICS.COUNT) {
			return DUAL_CHART_PANELS.filter((panel) => panel.key.startsWith('count'));
		}
		if (isLineCompareAmountMetric(yAxisMetric)) {
			return DUAL_CHART_PANELS.filter((panel) => panel.key.startsWith('amount'));
		}
		return buildDualFullPeriodPanels(yAxisMetric);
	}

	if (yAxisMetric === ORDER_PRIMARY_PHASE_METRICS.COUNT || isLineCompareAmountMetric(yAxisMetric)) {
		return CHART_PANELS.filter((panel) => panel.key === (isLineCompareAmountMetric(yAxisMetric) ? 'amount' : 'count'));
	}

	return [buildSingleMetricPanel(yAxisMetric)];
};
